<div class="black-checkbox container-tab-lists kv-page">
  <h4>{{title}}</h4>

  <div class="inner-tcontainer-tab">
    <div class="tab-body">
      <div class="tab-content">

        <div class="kv-form-container" [formGroup]="form">

          <!-- Lockable settings region -->
          <div [ngClass]="{'kv-locked-region': isLocked, 'kv-unlocked-region': !isLocked}">
            <div class="kv-region-header">
              <button mat-icon-button
                type="button"
                (click)="toggleEditMode()"
                [attr.aria-label]="isLocked ? 'Unlock PKI Region' : 'Lock PKI Region'"
                class="kv-lock-btn"
                [ngClass]="{'kv-locked-icon': isLocked, 'kv-unlocked-icon': !isLocked}"
                [title]="(isLocked ? 'Unlock' : 'Lock') + ' PKI Region'">
                <mat-icon>{{isLocked ? 'lock' : 'lock_open'}}</mat-icon>
              </button>
            </div>

            <div class="kv-region-content">
              <mat-form-field appearance="outline">
                <mat-label>Key Vault</mat-label>
                <mat-select formControlName="VaultName">
                  @for (vaultName of vaultNames; track vaultName) {
                    <mat-option [value]="vaultName">{{vaultName}}</mat-option>
                  }
                </mat-select>
                @if (form.get('VaultName')?.hasError('required')) {
                  <mat-error>Required</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>PKI Region</mat-label>
                <input matInput formControlName="PkiRegion" placeholder="e.g. ea" />
                @if (form.get('PkiRegion')?.hasError('required')) {
                  <mat-error>Required</mat-error>
                }
                @if (form.get('PkiRegion')?.hasError('maxlength')) {
                  <mat-error>Max 10 characters</mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <mat-form-field appearance="outline">
            <mat-label>MAC Address</mat-label>
            <input matInput formControlName="MacAddress" placeholder="e.g. A0:B1:C2:D3:E4:F5" />
            @if (form.get('MacAddress')?.hasError('required')) {
              <mat-error>Required</mat-error>
            }
            @if (form.get('MacAddress')?.hasError('pattern')) {
              <mat-error>Format: XX:XX:XX:XX:XX:XX</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Unit ID</mat-label>
            <input matInput formControlName="UnitId" placeholder="e.g. TQD009" />
            @if (form.get('UnitId')?.hasError('required')) {
              <mat-error>Required</mat-error>
            }
            @if (form.get('UnitId')?.hasError('maxlength')) {
              <mat-error>Max 50 characters</mat-error>
            }
          </mat-form-field>

          <div class="kv-secret-preview">
            <span>Secrets to operate on:</span>
            <code>{{secretBaseName}}-PUBLIC</code>
            <code>{{secretBaseName}}-PRIVATE</code>
          </div>

          <div class="kv-actions">
            <button mat-raised-button color="primary" type="button" class="kv-search-btn"
              (click)="onSearch()" [disabled]="isLoading || form.invalid">
              Search
            </button>
            <button mat-raised-button color="warn" type="button" class="kv-delete-btn"
              (click)="onDeleteConfirm()" [disabled]="isLoading || form.invalid">
              Delete &amp; Purge
            </button>
          </div>

          <!-- Loading spinner -->
          @if (isLoading) {
            <div class="kv-loading">
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
              <span>Processing...</span>
            </div>
          }

          <!-- Results region (aria-live lets screen readers announce updates) -->
          <div aria-live="polite" aria-atomic="false">

            <!-- Search results -->
            @if (searchResults.length > 0) {
              <div class="kv-results">
                <h5>Search Results</h5>
                <table mat-table [dataSource]="searchResults" class="mat-elevation-z2 kv-search-results-table">
                  <ng-container matColumnDef="SecretName">
                    <th mat-header-cell *matHeaderCellDef>Secret Name</th>
                    <td mat-cell *matCellDef="let row">{{row.SecretName}}</td>
                  </ng-container>
                  <ng-container matColumnDef="Status">
                    <th mat-header-cell *matHeaderCellDef>Status</th>
                    <td mat-cell *matCellDef="let row">
                      <span [class.kv-status-error]="isStatusError(row.Status)">
                        {{getStatusMessage(row.Status)}}
                      </span>
                      @if (row.Detail) {
                        <span class="kv-status-detail"> — {{row.Detail}}</span>
                      }
                      @if (row.TechnicalError) {
                        <span class="kv-status-error kv-status-detail"> ({{row.TechnicalError}})</span>
                      }
                    </td>
                  </ng-container>
                  <tr mat-header-row *matHeaderRowDef="['SecretName','Status']"></tr>
                  <tr mat-row *matRowDef="let row; columns: ['SecretName','Status']"></tr>
                </table>
              </div>
            }

            <!-- Operation results -->
            @if (operationResults.length > 0) {
              <div class="kv-results">
                <h5>Delete &amp; Purge Results</h5>
                <table mat-table [dataSource]="operationResults" class="mat-elevation-z2">
                  <ng-container matColumnDef="SecretName">
                    <th mat-header-cell *matHeaderCellDef>Secret Name</th>
                    <td mat-cell *matCellDef="let row">{{row.SecretName}}</td>
                  </ng-container>
                  <ng-container matColumnDef="DeleteStatus">
                    <th mat-header-cell *matHeaderCellDef>Delete</th>
                    <td mat-cell *matCellDef="let row">
                      <div class="kv-operation-cell">
                        <span [class.kv-status-error]="isOperationError(row.DeleteStatus)">
                          {{getOperationMessage(row.DeleteStatus, 'Delete')}}
                        </span>
                        @if (hasOperationDetail(row.DeleteStatus, row.DeleteDetail)) {
                          <button
                            mat-icon-button type="button"
                            [matTooltip]="row.DeleteDetail"
                            matTooltipPosition="above"
                            aria-label="Show technical delete detail">
                            <mat-icon>info_outline</mat-icon>
                          </button>
                        }
                      </div>
                    </td>
                  </ng-container>
                  <ng-container matColumnDef="PurgeStatus">
                    <th mat-header-cell *matHeaderCellDef>Purge</th>
                    <td mat-cell *matCellDef="let row">
                      <div class="kv-operation-cell">
                        <span [class.kv-status-error]="isOperationError(row.PurgeStatus)">
                          {{getOperationMessage(row.PurgeStatus, 'Purge')}}
                        </span>
                        @if (hasOperationDetail(row.PurgeStatus, row.PurgeDetail)) {
                          <button
                            mat-icon-button type="button"
                            [matTooltip]="row.PurgeDetail"
                            matTooltipPosition="above"
                            aria-label="Show technical purge detail">
                            <mat-icon>info_outline</mat-icon>
                          </button>
                        }
                      </div>
                    </td>
                  </ng-container>
                  <tr mat-header-row *matHeaderRowDef="['SecretName','DeleteStatus','PurgeStatus']"></tr>
                  <tr mat-row *matRowDef="let row; columns: ['SecretName','DeleteStatus','PurgeStatus']"></tr>
                </table>
              </div>
            }

            <!-- General message -->
            @if (resultMessage) {
              <div
                class="kv-message"
                [ngClass]="isResultMessageError ? 'kv-message-error' : 'kv-message-success'">
                {{resultMessage}}
              </div>
            }
            @if (technicalErrorMessage) {
              <div class="kv-technical-details">
                <button mat-button type="button" (click)="toggleTechnicalDetails()">
                  {{showTechnicalDetails ? 'Hide technical details' : 'Show technical details'}}
                </button>
                @if (showTechnicalDetails) {
                  <pre>{{technicalErrorMessage}}</pre>
                }
              </div>
            }

          </div><!-- /aria-live -->
        </div>

      </div>
    </div>
  </div>
</div>
